import React, { JSX } from "react";
import { Box, Button, Typography } from "@mui/material";
import { push } from "connected-react-router";
import Panel from "../../components/Panel";
import useFingerprint from "../../hooks/useFingerprint";
import { useAppDispatch } from "../../store/hooks";
import { clientFullNameWithPhoneSelector } from "../../reducers/client";

interface Props {
  onClose: () => void;
}

function FingerprintIdentifyPanel({ onClose }: Props): JSX.Element {
  const {
    deviceReady,
    imageB64,
    identifyState,
    loading,
    error,
    initialize,
    terminate,
    identify,
    resetIdentify
  } = useFingerprint();

  const dispatch = useAppDispatch();

  const handleIdentified = () => {
    if (identifyState.status === "matched" && identifyState.clientId) {
      dispatch(push(`/clients/${identifyState.clientId}`));
      onClose();
    }
  };

  return (
    <Panel title="Fingerprint Identification" onClose={onClose}>
      <Box px={2} maxWidth="500px">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box
            component="span"
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              flexShrink: 0,
              background: deviceReady ? "#22c55e" : "#ef4444"
            }}
          />
          <Typography sx={{ flex: 1, fontSize: 14, color: "#374151" }}>
            {deviceReady ? "Device ready" : "Device not connected"}
          </Typography>
          {!deviceReady ? (
            <Button variant="outlined" onClick={initialize} disabled={loading} size="small">
              Initialize Device
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={terminate}
              disabled={loading}
            >
              Close Device
            </Button>
          )}
        </Box>

        {error && (
          <Typography
            sx={{
              background: "#fee2e2",
              color: "#b91c1c",
              padding: "8px 12px",
              borderRadius: 1,
              fontSize: 13,
              marginBottom: 1
            }}
          >
            {error}
          </Typography>
        )}

        <Box
          sx={{
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            height: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 1,
            overflow: "hidden"
          }}
        >
          {imageB64 ? (
            <img
              src={`data:image/jpeg;base64,${imageB64}`}
              alt="Live scan"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <Typography sx={{ color: "#9ca3af", fontSize: 13 }}>
              Live preview appears here
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            padding: 2
          }}
        >
          {identifyState.status === "idle" && (
            <Button
              color="primary"
              variant="outlined"
              fullWidth
              onClick={identify}
              disabled={!deviceReady}
            >
              Load Templates &amp; Start Identifying
            </Button>
          )}

          {identifyState.status === "loading_templates" && (
            <Typography variant="body2" gutterBottom>
              Fetching templates from server…
            </Typography>
          )}

          {identifyState.status === "waiting" && (
            <Typography variant="body2">Place finger on scanner…</Typography>
          )}

          {identifyState.status === "matched" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography color="success">Match found</Typography>
              {identifyState.client && (
                <Typography
                  color="success"
                  role="button"
                  onClick={handleIdentified}
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                >
                  {clientFullNameWithPhoneSelector(identifyState.client)}
                </Typography>
              )}
              {!identifyState.clientId && (
                <Typography variant="body2" gutterBottom>
                  FID {identifyState.fid} · Score {identifyState.score}
                </Typography>
              )}
              <Button variant="outlined" onClick={resetIdentify}>
                Identify Again
              </Button>
            </Box>
          )}

          {identifyState.status === "no_match" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="error">
                No match found
              </Typography>
              <Button onClick={resetIdentify}>Try Again</Button>
            </Box>
          )}

          {identifyState.status === "error" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="error">
                {identifyState.message}
              </Typography>
              <Button onClick={resetIdentify}>Retry</Button>
            </Box>
          )}
        </Box>
      </Box>
    </Panel>
  );
}

export default FingerprintIdentifyPanel;
